import React from 'react';

import { Box, Text } from '@betterment/samba-react';

import { EnvelopeRow } from './EnvelopeRow';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdEnvelopeYtdReturnsQuery } from './graphql/query.householdEnvelopeYtdReturns.generated';
import { HeaderCell } from './HeaderCell';

type HouseholdData = Extract<HouseholdAccountsQuery['household'], { __typename: 'Household' }>;
type Envelope = HouseholdData['accounts'][number]['envelope'];
type AccountHolder = HouseholdData['accounts'][number]['accountHolders'][number];
type HouseholdEnvelopeYtd = Extract<HouseholdEnvelopeYtdReturnsQuery['household'], { __typename: 'Household' }>;

export const EnvelopesTable: React.FC<{
  envelopes: Envelope[];
  accountHoldersMap: Map<string, AccountHolder[]>;
  accountsCountMap: Map<string, number>;
  ytdReturnsData?: HouseholdEnvelopeYtd['envelopeYtdReturns'];
  ytdReturnsLoading: boolean;
  showYtdReturns: boolean;
}> = ({ envelopes, accountHoldersMap, accountsCountMap, ytdReturnsData, ytdReturnsLoading, showYtdReturns }) => {
  const gridColumns = showYtdReturns
    ? '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 200px) minmax(auto, 36px)'
    : '120px minmax(172px, 1fr) minmax(172px, 1fr) minmax(auto, 200px) minmax(auto, 36px)';

  const ytdReturnsMap = new Map(ytdReturnsData?.map(ytd => [ytd.envelopeId, ytd.ytdReturn]) ?? []);

  const tableHeaderStyling = {
    display: 'flex',
    alignItems: 'center',

    '& span': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 'size.300',
    },
  };

  return (
    <Box
      css={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gridAutoRows: 'auto',
        alignItems: 'end',
      }}
    >
      <HeaderCell css={{ gridColumn: '1', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Client(s)
        </Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '2', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Goal
        </Text>
      </HeaderCell>
      <HeaderCell css={{ gridColumn: '3', ...tableHeaderStyling }}>
        <Text variant="text50" color="text.weak">
          Target
        </Text>
      </HeaderCell>
      {showYtdReturns && (
        <HeaderCell css={{ textAlign: 'right', gridColumn: '4', justifyContent: 'end', ...tableHeaderStyling }}>
          <Text variant="text50" color="text.weak">
            Money-weighted return (YTD)
          </Text>
        </HeaderCell>
      )}
      <HeaderCell
        css={{
          textAlign: 'right',
          gridColumn: showYtdReturns ? '5' : '4',
          justifyContent: 'end',
          pr: 'space.300',
          ...tableHeaderStyling,
        }}
      >
        <Text variant="text50" color="text.weak">
          {showYtdReturns ? 'Balance (Earnings YTD)' : 'Balance'}
        </Text>
      </HeaderCell>
      {envelopes.map(envelope => {
        const ytdReturn = ytdReturnsMap.get(envelope.id);
        return (
          <EnvelopeRow
            key={envelope.id}
            envelope={envelope}
            accountHolders={accountHoldersMap.get(envelope.id) || []}
            accountCount={accountsCountMap.get(envelope.id) ?? 0}
            ytdReturn={ytdReturn}
            ytdReturnsLoading={ytdReturnsLoading}
            showYtdReturns={showYtdReturns}
          />
        );
      })}
    </Box>
  );
};
